import * as THREE from 'three';

/**
 * 📱 响应式处理工具 - V2X可视化的智能屏幕适配专家
 * =====================================================
 *
 * 🎭 【概念比喻】
 * 想象一下电影院的智能投影系统：
 * • 自动检测屏幕尺寸并调整画面比例
 * • 根据观众席位置优化最佳观看角度
 * • 无论是IMAX巨幕还是家庭影院都能完美呈现
 * • 就像液体一样自然地适应各种容器形状
 *
 * 🔬 【技术原理】
 * • 监听浏览器窗口尺寸变化事件（resize、orientationchange）
 * • 使用防抖技术避免频繁触发重绘，提升性能
 * • 智能宽高比保持，确保3D场景不变形
 * • 自适应像素密度，在高分屏上保持清晰度
 *
 * 🎯 【应用场景】
 * • 多设备V2X道路可视化（手机、平板、桌面、大屏）
 * • 自适应仪表盘和控制面板
 * • 嵌入式交通监控系统界面
 * • 响应式3D数据可视化展示
 *
 * 📊 【核心优势】
 * • 🔄 实时响应：毫秒级响应窗口变化
 * • 🎯 智能防抖：避免性能浪费的频繁重绘
 * • 📐 比例保持：确保3D场景始终显示正确
 * • 📱 跨平台：完美支持移动端和桌面端
 */

/**
 * ⚙️ 响应式配置接口 - 自定义适配规则
 *
 * 🎭 【比喻】就像影院的放映设置：
 * • enableAutoResize：是否启用自动画面调整
 * • debounceTime：画面稳定等待时间（避免抖动）
 * • maintainAspectRatio：是否保持画面比例不变形
 * • 尺寸限制：设定最小/最大显示尺寸边界
 */
export interface ResponsiveConfig {
  enableAutoResize?: boolean; // 🔄 自动尺寸调整开关
  debounceTime?: number; // ⏱️ 防抖延迟时间（毫秒）
  maintainAspectRatio?: boolean; // 📐 保持宽高比
  minWidth?: number; // 📏 最小宽度限制
  minHeight?: number; // 📏 最小高度限制
  maxWidth?: number; // 📏 最大宽度限制
  maxHeight?: number; // 📏 最大高度限制
}

/**
 * 📱 响应式管理器类 - 智能屏幕适配指挥官
 *
 * 🎭 【职业比喻】
 * 这个类就像一位经验丰富的影院技术总监：
 * • 实时监控放映设备状态（屏幕尺寸变化）
 * • 自动调整投影参数确保最佳观影效果
 * • 协调各种设备（渲染器、相机）同步工作
 * • 为不同观众（设备）提供个性化的视觉体验
 *
 * 🔧 【核心功能】
 * • 🔍 自动检测窗口尺寸变化
 * • 📐 智能宽高比计算和保持
 * • 📱 高分辨率屏幕适配（Retina等）
 * • ⏱️ 防抖优化减少不必要的重绘
 * • 🔔 提供尺寸变化事件回调机制
 * • 🎯 手动尺寸设置支持
 *
 * @example
 * ```typescript
 * // 创建响应式管理器
 * const responsiveManager = new ResponsiveManager(renderer, camera, {
 *   enableAutoResize: true,       // 启用自动调整
 *   debounceTime: 150,           // 150ms防抖
 *   maintainAspectRatio: true,   // 保持比例
 *   minWidth: 320,               // 最小宽度
 *   maxWidth: 3840               // 4K最大宽度
 * });
 *
 * // 监听尺寸变化
 * responsiveManager.onResize(() => {
 *   console.log('屏幕尺寸已更新');
 *   updateUI();
 * });
 *
 * // 手动触发尺寸更新
 * responsiveManager.updateSize();
 * ```
 */
export class ResponsiveManager {
  // 🎬 核心组件引用
  private renderer: THREE.WebGLRenderer; // WebGL渲染器
  private camera: THREE.PerspectiveCamera; // 透视相机
  private config: ResponsiveConfig; // 配置选项

  // 🎮 事件管理
  private resizeCallbacks: (() => void)[] = []; // 尺寸变化回调函数列表
  private debounceTimer: number | null = null; // 防抖计时器

  /**
   * 🏗️ 构造函数 - 搭建智能适配系统
   *
   * 🎭 【比喻】就像安装一套智能影院系统：
   * • 连接投影设备（渲染器）
   * • 校准摄像设备（相机）
   * • 配置自动调节规则（配置选项）
   * • 启动实时监控系统
   *
   * 🔧 【初始化流程】
   * 1. 保存核心组件的引用
   * 2. 合并用户配置与默认配置
   * 3. 根据配置决定是否启用自动响应
   * 4. 执行初始尺寸设置
   *
   * 💡 **默认配置说明**：
   * • enableAutoResize=true：现代Web应用标配
   * • debounceTime=100ms：平衡响应速度与性能
   * • maintainAspectRatio=true：避免3D场景变形
   * • 尺寸限制：覆盖主流设备分辨率范围
   *
   * @param renderer - 🖥️ WebGL渲染器实例
   * @param camera - 📷 透视相机实例
   * @param config - ⚙️ 响应式配置选项
   */
  constructor(
    renderer: THREE.WebGLRenderer,
    camera: THREE.PerspectiveCamera,
    config: ResponsiveConfig = {}
  ) {
    // 🔗 保存核心组件引用
    this.renderer = renderer;
    this.camera = camera;

    // 📋 合并配置（用户配置覆盖默认值）
    this.config = {
      enableAutoResize: true, // 🔄 默认启用自动调整
      debounceTime: 100, // ⏱️ 100ms防抖时间
      maintainAspectRatio: true, // 📐 默认保持宽高比
      minWidth: 320, // 📱 最小宽度（iPhone SE）
      minHeight: 240, // 📱 最小高度
      maxWidth: window.screen.width, // 🖥️ 屏幕最大宽度
      maxHeight: window.screen.height, // 🖥️ 屏幕最大高度
      ...config, // 🔄 用户自定义配置覆盖
    };

    // 🚀 根据配置启用自动响应功能
    if (this.config.enableAutoResize) {
      this.initAutoResize();
    }
  }

  /**
   * 🔄 初始化自动响应 - 部署智能监控系统
   *
   * 🎭 【比喻】就像给影院安装运动传感器：
   * • 监听窗口大小变化（观众席调整）
   * • 监听设备方向改变（手机旋转）
   * • 设置防抖机制避免过度敏感
   * • 执行初始画面调整
   *
   * 🔬 【技术实现】
   * • resize事件：窗口大小改变时触发
   * • orientationchange事件：移动设备旋转时触发
   * • 防抖处理：避免拖拽窗口时的频繁重绘
   * • 初始化调用：确保启动时就有正确尺寸
   *
   * ⚡ **性能优化**：
   * • 防抖技术减少不必要的渲染调用
   * • 事件委托避免内存泄漏
   * • 智能判断避免无效的尺寸更新
   *
   * @private
   */
  private initAutoResize() {
    // 🎯 定义防抖处理函数
    const handleResize = () => {
      // 🛑 清除之前的计时器（重新开始计时）
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // ⏱️ 设置新的防抖计时器
      this.debounceTimer = window.setTimeout(() => {
        this.updateSize(); // 🔄 执行尺寸更新
      }, this.config.debounceTime);
    };

    // 👂 监听窗口尺寸变化事件
    window.addEventListener('resize', handleResize);

    // 📱 监听移动设备方向变化事件
    window.addEventListener('orientationchange', handleResize);

    // 🚀 初始设置（确保启动时尺寸正确）
    this.updateSize();
  }

  /**
   * 🔄 更新尺寸 - 执行智能画面调整
   *
   * 🎭 【比喻】就像影院技师调整投影参数：
   * • 测量当前屏幕尺寸
   * • 应用尺寸限制规则
   * • 计算最佳宽高比
   * • 同步更新所有设备参数
   * • 通知相关系统响应变化
   *
   * 🔬 【处理流程】
   * 1. 获取窗口当前尺寸
   * 2. 应用配置的尺寸限制
   * 3. 根据需要保持宽高比
   * 4. 更新渲染器画布尺寸
   * 5. 更新相机投影矩阵
   * 6. 触发回调通知外部系统
   *
   * 📐 **宽高比保持算法**：
   * • 计算当前和目标宽高比
   * • 选择较小的缩放因子避免裁剪
   * • 确保3D场景在任何屏幕上都不变形
   *
   * ⚡ **性能考虑**：
   * • 像素比限制为2避免过度渲染
   * • 批量更新减少GPU状态切换
   * • 智能日志只在调试时输出
   *
   * @public
   */
  updateSize() {
    // 📏 获取窗口当前尺寸
    let width = window.innerWidth;
    let height = window.innerHeight;

    // 🔒 应用尺寸限制（防止极端尺寸）
    if (this.config.minWidth) {
      width = Math.max(width, this.config.minWidth);
    }
    if (this.config.maxWidth) {
      width = Math.min(width, this.config.maxWidth);
    }
    if (this.config.minHeight) {
      height = Math.max(height, this.config.minHeight);
    }
    if (this.config.maxHeight) {
      height = Math.min(height, this.config.maxHeight);
    }

    // 📐 保持宽高比（避免3D场景变形）
    if (this.config.maintainAspectRatio) {
      const aspectRatio = this.camera.aspect; // 🎯 相机原始宽高比
      const currentAspectRatio = width / height; // 📊 当前窗口宽高比

      // 🔍 选择合适的缩放策略
      if (currentAspectRatio > aspectRatio) {
        // 📏 窗口太宽，缩小宽度
        width = height * aspectRatio;
      } else {
        // 📏 窗口太高，缩小高度
        height = width / aspectRatio;
      }
    }

    // 🖥️ 更新渲染器尺寸
    this.renderer.setSize(width, height);

    // 📱 设置像素密度（限制最大值避免性能问题）
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // 📷 更新相机参数
    this.camera.aspect = width / height; // 🔄 更新宽高比
    this.camera.updateProjectionMatrix(); // 🔄 重新计算投影矩阵

    // 📊 输出调试信息
    console.log(
      `[V2X-Three] 尺寸更新: ${width}x${height}, 像素比: ${this.renderer.getPixelRatio()}`
    );

    // 🔔 执行回调通知外部系统
    this.resizeCallbacks.forEach(callback => callback());
  }

  /**
   * 🔔 添加尺寸变化回调 - 注册观察者
   *
   * 🎭 【比喻】就像给影院安装通知系统：
   * • 当屏幕调整完成时自动通知相关部门
   * • 音响师调整音效、灯光师调整照明
   * • 每个部门都能及时响应屏幕变化
   * • 提供取消订阅机制避免内存泄漏
   *
   * 🔬 【设计模式】
   * • 观察者模式：一对多的依赖关系
   * • 回调注册：动态添加响应逻辑
   * • 取消机制：返回取消函数防止内存泄漏
   * • 类型安全：TypeScript支持
   *
   * 💡 **使用场景**：
   * • UI组件重新布局
   * • 图表和控件尺寸调整
   * • 自定义渲染逻辑更新
   * • 性能监控和日志记录
   *
   * @param callback - 🎯 尺寸变化时的回调函数
   * @returns {Function} 🗑️ 取消订阅的函数
   *
   * @example
   * ```typescript
   * // 注册回调
   * const unsubscribe = responsiveManager.onResize(() => {
   *   // 重新布局UI组件
   *   updateControlPanel();
   *   resizeChart();
   * });
   *
   * // 组件销毁时取消订阅
   * onDestroy(() => {
   *   unsubscribe();
   * });
   * ```
   */
  onResize(callback: () => void) {
    // 📝 将回调函数添加到列表
    this.resizeCallbacks.push(callback);

    // 🔄 返回取消订阅函数（闭包保持引用）
    return () => {
      const index = this.resizeCallbacks.indexOf(callback);
      if (index > -1) {
        this.resizeCallbacks.splice(index, 1); // 🗑️ 从列表中移除
      }
    };
  }

  /**
   * 📊 获取当前尺寸 - 状态查询接口
   *
   * 🎭 【比喻】就像查看影院的技术参数面板：
   * • 屏幕分辨率（宽度x高度）
   * • 像素密度（清晰度等级）
   * • 画面比例（宽高比）
   * • 实时状态一目了然
   *
   * 🔬 【返回信息】
   * • width/height：当前渲染尺寸
   * • pixelRatio：实际像素密度
   * • aspect：相机宽高比
   *
   * 💡 **用途示例**：
   * • 动态UI布局计算
   * • 性能监控和优化
   * • 截图功能尺寸确定
   * • 调试和故障排查
   *
   * @returns {Object} 📋 当前尺寸信息对象
   */
  getCurrentSize() {
    return {
      width: this.renderer.domElement.width, // 🖥️ 渲染宽度
      height: this.renderer.domElement.height, // 🖥️ 渲染高度
      pixelRatio: this.renderer.getPixelRatio(), // 📱 像素密度
      aspect: this.camera.aspect, // 📐 宽高比
    };
  }

  /**
   * 🎯 手动设置尺寸 - 精确控制模式
   *
   * 🎭 【比喻】就像手动调节投影仪参数：
   * • 关闭自动模式，使用专业手动控制
   * • 精确设定投影画面尺寸
   * • 适用于特殊显示需求和嵌入式系统
   * • 立即应用设置并通知相关系统
   *
   * 🔬 【应用场景】
   * • 嵌入式3D组件固定尺寸
   * • 截图功能指定输出尺寸
   * • 多屏显示系统精确控制
   * • 自定义布局管理
   *
   * ⚠️ **注意事项**：
   * • 手动设置会覆盖自动响应结果
   * • 需要确保尺寸参数的合理性
   * • 建议在禁用自动响应时使用
   *
   * @param width - 🖥️ 目标宽度（像素）
   * @param height - 🖥️ 目标高度（像素）
   *
   * @example
   * ```typescript
   * // 设置固定尺寸用于截图
   * responsiveManager.setSize(1920, 1080);
   *
   * // 嵌入式组件固定尺寸
   * responsiveManager.setSize(800, 600);
   * ```
   */
  setSize(width: number, height: number) {
    // 🖥️ 更新渲染器尺寸
    this.renderer.setSize(width, height);

    // 📷 更新相机参数
    this.camera.aspect = width / height; // 🔄 计算新的宽高比
    this.camera.updateProjectionMatrix(); // 🔄 重新计算投影矩阵

    // 🔔 触发回调通知外部系统
    this.resizeCallbacks.forEach(callback => callback());
  }

  /**
   * 🗑️ 销毁响应式管理器 - 清理监控系统
   *
   * 🎭 【比喻】就像影院关门前的设备回收：
   * • 关闭所有监控传感器
   * • 清理定时器和计划任务
   * • 移除所有事件监听器
   * • 释放内存避免泄漏
   *
   * 🔬 【清理内容】
   * • 防抖计时器：避免延迟执行
   * • 回调函数列表：防止内存泄漏
   * • 事件监听器：清理浏览器事件
   *
   * ⚠️ **重要性**：
   * • 特别重要在单页应用(SPA)中
   * • 避免组件卸载后仍然响应事件
   * • 防止内存累积导致性能下降
   *
   * @public
   */
  dispose() {
    // ⏱️ 清理防抖计时器
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // 🗑️ 清空回调函数列表
    this.resizeCallbacks.length = 0;
  }
}

/**
 * 🎉 ResponsiveManager 使用总结
 * ========================================
 *
 * 🌟 **核心优势**：
 * • 🔄 智能响应：毫秒级检测窗口变化
 * • 📐 比例保持：确保3D场景永不变形
 * • ⚡ 性能优化：防抖技术避免频繁重绘
 * • 📱 跨平台：完美支持移动端和桌面端
 * • 🎯 精确控制：支持手动尺寸设置
 *
 * 🎨 **技术特色**：
 * • 🛡️ 防抖算法减少性能开销
 * • 📊 智能宽高比计算和保持
 * • 🔔 观察者模式的回调机制
 * • 📏 灵活的尺寸限制配置
 * • 💾 完善的资源清理机制
 *
 * ⚡ **性能优化**：
 * • 防抖技术减少不必要的渲染
 * • 像素密度智能限制
 * • 批量状态更新减少GPU调用
 * • 内存泄漏防护机制
 * • 事件监听器智能管理
 *
 * 🎯 **应用场景**：
 * • V2X交通可视化多设备适配
 * • 响应式3D仪表盘开发
 * • 嵌入式3D组件系统
 * • 多屏显示解决方案
 * • 移动端3D应用开发
 *
 * 💡 **最佳实践**：
 * • 生产环境建议启用防抖优化
 * • 移动端适当设置像素密度上限
 * • 复杂界面使用回调机制同步更新
 * • 组件销毁时及时调用dispose()
 * • 根据设备性能调整防抖时间
 */
